<template>
  <div class="bg pb-16 p-3 mb-auto h-full overflow-hidden bg-gradient-to-b bg-gray-800 flex flex-col justify-between" id="style-1">
    <!-- Main Content -->
    <main class="flex-grow flex items-center justify-center pb-20 sm:pb-0">
      <div v-if="isLoading" class="flex flex-col items-center">
        <div class="animate-spin rounded-full h-10 w-10 border-t-4 border-indigo-500 mb-3"></div>
        <p class="text-gray-300 text-sm font-zombie">กำลังค้นหาอาชญากร...</p>
      </div>
      <div v-else-if="error" class="text-center text-red-400 text-xl font-zombie">
        {{ error }}
      </div>
      <div v-else-if="playerRoom === 1" class="text-center flex flex-col items-center animate-fade-in">
        <i class="fas fa-skull-crossbones text-6xl text-gray-400 mb-4"></i>
        <p class="text-gray-300 text-xl font-zombie mb-4">ไม่สามารถค้นหาอาชญากรได้</p>
        <p class="text-gray-500 text-sm font-zombie">อยู่ในห้อง 1 ไม่สามารถค้นหาได้</p>
      </div>
      <div v-else-if="!assassinateCriminalId" class="text-center flex flex-col items-center animate-fade-in">
        <i class="fas fa-skull-crossbones text-6xl text-gray-400 mb-4"></i>
        <p class="text-gray-300 text-xl font-zombie mb-4">กรุณาค้นหาอาชญากรเพื่อลอบสังหาร</p>
        <p class="text-gray-500 text-sm font-zombie">เลือกจำนวนห้องเพื่อค้นหาอาชญากร</p>
        <!-- Roll Button -->
        <div class="mt-2 w-full max-w-xs">
          <button
            @click="openSearchModal"
            :disabled="isLoading || character.silver < 100 || playerRoom === 1"
            class="w-full py-3 rounded-md text-white font-bold text-sm font-zombie transition-all duration-300 flex items-center justify-center gap-2"
            :class="isLoading || character.silver < 100 || playerRoom === 1 ? 'bg-gray-600 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'"
          >
            <i class="fas fa-redo"></i>
            ค้นหาอาชญากรใหม่ (100 Silver)
          </button>
        </div>
      </div>
      <div v-else-if="!criminal" class="text-center flex flex-col items-center animate-fade-in">
        <i class="fas fa-skull-crossbones text-6xl text-gray-400 mb-4"></i>
        <p class="text-gray-300 text-xl font-zombie mb-4">ไม่พบอาชญากรในห้องที่เลือก</p>
        <p class="text-gray-500 text-sm font-zombie">ลองค้นหาใหม่</p>
        <!-- Roll Button -->
        <div class="mt-2 w-full max-w-xs">
          <button
            @click="handleRefresh"
            :disabled="isLoading || character.silver < 100 || playerRoom === 1"
            class="w-full py-3 rounded-md text-white font-bold text-sm font-zombie transition-all duration-300 flex items-center justify-center gap-2"
            :class="isLoading || character.silver < 100 || playerRoom === 1 ? 'bg-gray-600 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'"
          >
            <i class="fas fa-redo"></i>
            ค้นหาอาชญากรใหม่ (100 Silver)
          </button>
        </div>
      </div>
      <div v-else class="w-full max-w-md">
        <!-- Criminal Card -->
        <div 
          class="zombie-card bg-gray-900 bg-opacity-80 rounded-xl shadow-lg overflow-hidden transform transition-all duration-300"
          @click="openModal(criminal.docId)"
        >
          <!-- Card Image -->
          <div class="relative">
            <img 
              :src="criminal.imageUrl || 'https://via.placeholder.com/150'" 
              alt="Criminal Image" 
              class="w-full h-80 object-cover" 
            />
            <!-- Refresh Button -->
            <button
              @click.stop="handleRefresh"
              :disabled="isLoading || character.silver < 100 || playerRoom === 1"
              class="absolute top-2 left-2 bg-indigo-600 text-white p-2 rounded-full hover:bg-indigo-700 transition-all duration-300"
              :class="isLoading || character.silver < 100 || playerRoom === 1 ? 'bg-gray-600 cursor-not-allowed' : ''"
              title="ค้นหาอาชญากรใหม่ (100 Silver)"
            >
              <i class="fas fa-redo"></i>
            </button>
            <!-- Change Room Button -->
            <button
              @click.stop="openSearchModal"
              :disabled="isLoading || playerRoom === 1"
              class="absolute top-2 left-12 bg-green-600 text-white p-2 rounded-full hover:bg-green-700 transition-all duration-300"
              :class="isLoading || playerRoom === 1 ? 'bg-gray-600 cursor-not-allowed' : ''"
              title="เปลี่ยนจำนวนห้อง"
            >
              <i class="fas fa-exchange-alt"></i>
            </button>
            <div class="absolute top-2 right-2 bg-purple-600 text-white px-2 py-1 rounded-full text-xs font-zombie font-bold">
              LV {{ criminal.level || 'N/A' }}
            </div>
          </div>

          <!-- Card Content -->
          <div class="p-4">
            <div class="flex justify-between items-center mb-1">
              <h3 class="text-xl font-bold text-purple-300 font-zombie truncate">{{ criminal.name || 'Unknown' }}</h3>
              <p class="text-sm text-gray-400 font-zombie mb-3">จำนวนห้อง {{ criminal.room }}</p>
            </div>
            
            <!-- Rewards -->
            <div class="flex flex-wrap gap-2">
              <span v-if="criminal.renownReward > 0" class="flex items-center bg-gray-800 px-2 py-1 rounded-full text-xs text-indigo-300">
                <i class="fas fa-star mr-1"></i>{{ criminal.renownReward }}
              </span>
              <span v-if="criminal.silverReward > 0" class="flex items-center bg-gray-800 px-2 py-1 rounded-full text-xs text-gray-300">
                <i class="fas fa-coins mr-1"></i>{{ criminal.silverReward }}
              </span>
              <span v-if="criminal.goldReward > 0" class="flex items-center bg-gray-800 px-2 py-1 rounded-full text-xs text-yellow-400">
                <i class="fas fa-g mr-1"></i>{{ criminal.goldReward }}
              </span>
              <span v-if="criminal.diamondReward > 0" class="flex items-center bg-gray-800 px-2 py-1 rounded-full text-xs text-blue-400">
                <i class="fas fa-gem mr-1"></i>{{ criminal.diamondReward }}
              </span>
            </div>

            <!-- Roll Button -->
            <div class="mt-4">
              <button
                @click.stop="handleRefresh"
                :disabled="isLoading || character.silver < 100 || playerRoom === 1"
                class="w-full py-3 rounded-md text-white font-bold text-sm font-zombie transition-all duration-300 flex items-center justify-center gap-2"
                :class="isLoading || character.silver < 100 || playerRoom === 1 ? 'bg-gray-600 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'"
              >
                <i class="fas fa-redo"></i>
                ค้นหาอาชญากรใหม่ (100 Silver)
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- Search Criminal Modal -->
    <div v-if="isSearchModalOpen" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-gray-900 rounded-xl p-6 w-full max-w-sm">
        <h2 class="text-xl font-bold text-purple-300 font-zombie mb-4">ค้นหาอาชญากรใหม่</h2>
        <p class="text-gray-300 text-sm font-zombie mb-4">เลือกจำนวนห้องของอาชญากร</p>
        <select
          v-model="selectedRoom"
          class="w-full py-2 px-3 rounded-md bg-gray-700 text-white font-zombie text-sm border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500 mb-4"
        >
          <option value="" disabled>เลือกจำนวนห้อง</option>
          <option v-for="room in [2, 3]" :key="room" :value="room">{{ room }}</option>
        </select>
        <div class="flex justify-end gap-2">
          <button
            @click="closeSearchModal"
            class="py-2 px-4 rounded-md bg-gray-600 text-white font-zombie text-sm hover:bg-gray-700 transition-all duration-300"
          >
            ยกเลิก
          </button>
          <button
            @click="confirmSearch"
            :disabled="isLoading || character.silver < 100 || playerRoom === 1 || !selectedRoom"
            class="py-2 px-4 rounded-md text-white font-zombie text-sm transition-all duration-300"
            :class="isLoading || character.silver < 100 || playerRoom === 1 || !selectedRoom ? 'bg-gray-600 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700'"
          >
            ค้นหา
          </button>
        </div>
      </div>
    </div>

    <!-- CriminalModal -->
    <CriminalModal 
      v-if="isModalOpen" 
      :criminal-id="selectedCriminalId" 
      :is-open="isModalOpen"
      @close="closeModal" 
    />
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue';
import { projectFirestore, projectAuth, projectFunctions } from '@/firebase/config';
import CriminalModal from '@/components/CriminalModal.vue';

export default {
  name: 'CriminalLeaderboard',
  components: {
    CriminalModal
  },
  setup() {
    const criminal = ref(null);
    const character = ref({});
    const assassinateCriminalId = ref(null);
    const isLoading = ref(false);
    const error = ref(null);
    const isModalOpen = ref(false);
    const selectedCriminalId = ref(null);
    const playerRoom = ref(null);
    const userId = ref(null);
    const selectedRoom = ref('');
    const isSearchModalOpen = ref(false);

    let unsubscribeCharacter = null;
    let unsubscribePrivate = null;
    let unsubscribeCriminal = null;

    // Get search range (now just returns the selected room)
    const getSearchRange = () => {
      if (playerRoom.value === 1) {
        return 'ไม่สามารถค้นหาได้';
      }
      return selectedRoom.value || 'กรุณาเลือกห้อง';
    };

    // Fetch a random criminal via Cloud Function
    const fetchRandomCriminal = async () => {
      if (playerRoom.value === 1) {
        error.value = 'ไม่สามารถค้นหาอาชญากรจากห้อง 1';
        return;
      }
      if (!selectedRoom.value) {
        error.value = 'กรุณาเลือกจำนวนห้องก่อนค้นหา';
        return;
      }
      isLoading.value = true;
      error.value = null;
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        const result = await gameActions({ action: 'findRandomCriminal2', selectedRoom: selectedRoom.value });
        if (result.data.success && result.data.criminalId) {
          assassinateCriminalId.value = result.data.criminalId;
          isSearchModalOpen.value = false; // Close modal on success
        } else {
          criminal.value = null;
          assassinateCriminalId.value = null;
          error.value = result.data.message;
        }
      } catch (err) {
        error.value = 'เกิดข้อผิดพลาดในการค้นหาอาชญากร: ' + err.message;
        console.error('Error fetching criminal:', err);
      } finally {
        isLoading.value = false;
      }
    };

    // Handle refresh: if selectedRoom exists, search directly; else open modal
    const handleRefresh = () => {
      if (selectedRoom.value) {
        confirmSearch();
      } else {
        openSearchModal();
      }
    };

    // Open search modal
    const openSearchModal = () => {
      isSearchModalOpen.value = true;
    };

    // Close search modal
    const closeSearchModal = () => {
      isSearchModalOpen.value = false;
    };

    // Confirm search
    const confirmSearch = async () => {
      await fetchRandomCriminal();
    };

    // Open criminal modal
    const openModal = (docId) => {
      selectedCriminalId.value = docId;
      isModalOpen.value = true;
    };

    // Close criminal modal
    const closeModal = () => {
      isModalOpen.value = false;
      selectedCriminalId.value = null;
    };

    // Set up real-time listeners
    const setupRealtimeListeners = async () => {
      try {
        const user = projectAuth.currentUser;
        if (!user) {
          throw new Error('ผู้ใช้ไม่ได้ล็อกอิน');
        }
        userId.value = user.uid;

        // Listen to player character data
        unsubscribeCharacter = projectFirestore
          .collection('characters')
          .doc(userId.value)
          .onSnapshot(
            (doc) => {
              if (doc.exists) {
                character.value = doc.data();
                playerRoom.value = doc.data().room || 2;
              } else {
                character.value = {};
                playerRoom.value = 2;
                error.value = 'ไม่พบข้อมูลผู้เล่น';
              }
            },
            (err) => {
              error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลผู้เล่น: ' + err.message;
              console.error('Error in character snapshot:', err);
            }
          );

        // Listen to characterPrivate for assassinateCriminalId
        unsubscribePrivate = projectFirestore
          .collection('characterPrivate')
          .doc(userId.value)
          .onSnapshot(
            async (doc) => {
              if (doc.exists && doc.data().assassinateCriminalId) {
                assassinateCriminalId.value = doc.data().assassinateCriminalId;
                if (assassinateCriminalId.value && (!unsubscribeCriminal || criminal.value?.docId !== assassinateCriminalId.value)) {
                  if (unsubscribeCriminal) {
                    unsubscribeCriminal();
                    unsubscribeCriminal = null;
                  }
                  unsubscribeCriminal = projectFirestore
                    .collection('criminals')
                    .doc(assassinateCriminalId.value)
                    .onSnapshot(
                      (criminalDoc) => {
                        if (criminalDoc.exists) {
                          const data = criminalDoc.data();
                          criminal.value = {
                            docId: criminalDoc.id,
                            name: data.name,
                            level: data.level,
                            room: data.room,
                            imageUrl: data.imageUrl,
                            renownReward: data.renownReward,
                            silverReward: data.silverReward,
                            goldReward: data.goldReward,
                            diamondReward: data.diamondReward,
                            damageTaken: data.damageTaken
                          };
                        } else {
                          criminal.value = null;
                          assassinateCriminalId.value = null;
                        }
                      },
                      (err) => {
                        error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลอาชญากร: ' + err.message;
                        console.error('Error in criminal snapshot:', err);
                        criminal.value = null;
                        assassinateCriminalId.value = null;
                      }
                    );
                }
              } else {
                assassinateCriminalId.value = null;
                criminal.value = null;
                if (unsubscribeCriminal) {
                  unsubscribeCriminal();
                  unsubscribeCriminal = null;
                }
              }
            },
            (err) => {
              error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลส่วนตัว: ' + err.message;
              console.error('Error in private snapshot:', err);
            }
          );
      } catch (err) {
        error.value = 'เกิดข้อผิดพลาดในการตั้งค่าการฟังข้อมูล: ' + err.message;
        console.error('Error setting up listeners:', err);
      }
    };

    onMounted(() => {
      setupRealtimeListeners();
    });

    onUnmounted(() => {
      if (typeof unsubscribeCharacter === 'function') {
        unsubscribeCharacter();
      }
      if (typeof unsubscribePrivate === 'function') {
        unsubscribePrivate();
      }
      if (typeof unsubscribeCriminal === 'function') {
        unsubscribeCriminal();
      }
    });

    return {
      criminal,
      character,
      assassinateCriminalId,
      isLoading,
      error,
      isModalOpen,
      selectedCriminalId,
      openModal,
      closeModal,
      openSearchModal,
      closeSearchModal,
      confirmSearch,
      playerRoom,
      userId,
      selectedRoom,
      isSearchModalOpen,
      getSearchRange,
      handleRefresh
    };
  }
};
</script>

<style scoped>
.bg {
  background: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2F%E0%B8%AB%E0%B9%89%E0%B8%AD%E0%B8%87%E0%B9%82%E0%B8%96%E0%B8%87.png?alt=media&token=09a147d0-386c-4f34-8f4a-3165ae99e756') no-repeat center center fixed,
              linear-gradient(to bottom, rgba(10, 10, 10, 0.95), rgba(20, 20, 20, 0.98));
}

@font-face {
  font-family: 'Zombie';
  src: url('https://fonts.googleapis.com/css2?family=Creepster&display=swap');
}

.zombie-card {
  border: 1px solid rgba(75, 94, 154, 0.3);
}

.zombie-card:hover {
  border-color: rgba(75, 94, 154, 0.8);
  box-shadow: 0 0 15px rgba(75, 94, 154, 0.4);
}

@keyframes slideIn {
  from { opacity: 0; transform: translateY(100%); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.zombie-card {
  animation: slideIn 0.5s ease-out;
}

.animate-fade-in {
  animation: fadeIn 0.5s ease-out;
}
</style>